import { useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, X, Download, ChevronDown, ChevronUp, BarChart3, Table, Database, Maximize2, Minimize2, TrendingUp, Brain, Percent, Sigma, LayoutGrid, Divide, Filter, Play, Calendar } from 'lucide-react'
import { cleanColumnNames, cleanColumnName } from '@/utils/column-names'
import { useScriptStore } from '@/store/scriptStore'
import { useJobStore } from '@/store/jobStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import MonacoEditor from '@/components/editor/MonacoEditor'
import { createScriptSchema, updateScriptSchema, type CreateScriptInput, type UpdateScriptInput } from '@/schemas/script.schema'
import { showErrorToast, showSuccessToast } from '@/utils/error-handler'
import { LoadingPage } from '@/components/ui/loading-spinner'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { jobsApi } from '@/services/api/jobs'
import apiClient from '@/services/api/client'
import { exportToCSV, exportToJSON } from '@/utils/export'
import ResultsChart from '@/components/results/ResultsChart'
import { datasetsApi } from '@/services/api/datasets'
import type { DatasetMetadata } from '@/types/metadata'
import { BacktestConfigModal } from '@/components/backtest'
import BacktestTabs from '@/components/backtest/BacktestTabs'
import SignalRiskWidget from '@/components/signals/SignalRiskWidget'
import DatasetPreviewChart from '@/components/datasets/DatasetPreviewChart'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { isValidForSignalAnalysis, extractSignalNames } from '@/utils/dsl-helpers'
import { AIScriptGenerator, AIScriptEditor, type AIScriptEditorHandle } from '@/components/ai'
import AIConfigModal from '@/components/ai/AIConfigModal'
import AIResultsExplanation from '@/components/ai/AIResultsExplanation'
import { loadChatFromSession, saveChatToSession, parseChatFromJson } from '@/services/ai/chat-storage'
import { ActivityBar, TitleBar } from '@/components/scripts/editor'
import { isDataTooLargeForAI } from '@/utils/data-size'

export default function ScriptEditorPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentScript, loadScript, updateScript, createScript, isLoading } = useScriptStore()
  const { createJob } = useJobStore()
  const [isSaving, setIsSaving] = useState(false)
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const [resultsExpanded, setResultsExpanded] = useState(true)
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table')
  const [showAllRows, setShowAllRows] = useState(false)
  // Initialize bottom panel to hidden (0) - will expand when content is available
  const [bottomPanelHeight, setBottomPanelHeight] = useState(0)
  const [activeBottomTab, setActiveBottomTab] = useState<'results' | 'risk' | 'backtests' | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isSavingAndRunning, setIsSavingAndRunning] = useState(false)
  const [selectedDatasetId, setSelectedDatasetId] = useState<string>('')
  const [selectedSymbol, setSelectedSymbol] = useState<string>('')

  const [isEditorExpanded, setIsEditorExpanded] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [headerCollapsed, setHeaderCollapsed] = useState(false)
  const [sidebarWidth, setSidebarWidth] = useState(450) // Default 450px
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false)
  const [validationErrorModal, setValidationErrorModal] = useState<{ open: boolean; message: string }>({ open: false, message: '' })
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null)
  const [showDatasetPreview, setShowDatasetPreview] = useState(false)
  const [showBacktestConfig, setShowBacktestConfig] = useState(false)
  const [backtestJobId, setBacktestJobId] = useState<string | null>(null)
  const [isAIAnalysisOpen, setIsAIAnalysisOpen] = useState(false)
  const editorRef = useRef<{ editor: any; monaco: any } | null>(null)
  const aiScriptEditorRef = useRef<AIScriptEditorHandle | null>(null)
  /** When Save is clicked with code changes but AI panel is closed, store version here; AIScriptEditor will apply it when it mounts */
  const [pendingSavedVersion, setPendingSavedVersion] = useState<{ code: string; description: string } | null>(null)
  /** Filters for stats results: group column name → selected value (null = All) */
  const [statsFilters, setStatsFilters] = useState<Record<string, string | null>>({})
  /** Date range in Results tab for quick re-run; default last 1 year */
  const getDefaultDateRange = () => {
    const end = new Date()
    const start = new Date()
    start.setFullYear(start.getFullYear() - 1)
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return { start: fmt(start), end: fmt(end) }
  }
  const defaultRange = getDefaultDateRange()
  const [resultsDateStart, setResultsDateStart] = useState<string>(defaultRange.start)
  const [resultsDateEnd, setResultsDateEnd] = useState<string>(defaultRange.end)
  const [resultsDatePreset, setResultsDatePreset] = useState<string>('last_1_year')
  /** Params used for last Run Analysis (disable button until params change) */
  /** Unified AI chat (Script Generation + Chat) – single list, persisted by scriptId */
  const [unifiedChatMessages, setUnifiedChatMessages] = useState<import('@/services/ai/chat-storage').ChatMessage[]>(() => {
    const migrated = (location.state as { migratedChat?: string } | null)?.migratedChat
    const sid = id || 'new'
    if (migrated && sid !== 'new') {
      const parsed = parseChatFromJson(migrated)
      if (parsed?.length) return parsed
    }
    const fromSession = loadChatFromSession(sid)
    return fromSession ?? []
  })
  const [lastQuickRunParams, setLastQuickRunParams] = useState<{
    datasetId: string
    symbol: string
    dateStart: string
    dateEnd: string
  } | null>(null)

  // Paginated signals state
  const [signalsData, setSignalsData] = useState<any[]>([])
  const [signalsPage, setSignalsPage] = useState(1)
  const [signalsHasMore, setSignalsHasMore] = useState(false)
  const [isLoadingMoreSignals, setIsLoadingMoreSignals] = useState(false)
  const [signalsColumns, setSignalsColumns] = useState<string[]>([])

  // Load last selected dataset and symbol from localStorage
  useEffect(() => {
    const lastDatasetId = localStorage.getItem('lastSelectedDatasetId')
    if (lastDatasetId) {
      setSelectedDatasetId(lastDatasetId)
      // Load symbol selection for this dataset
      const savedSymbol = localStorage.getItem(`selectedSymbol_${lastDatasetId}`)
      if (savedSymbol) {
        setSelectedSymbol(savedSymbol)
      }
    }
  }, [])

  // Save selected dataset to localStorage
  const handleDatasetChange = (datasetId: string) => {
    setSelectedDatasetId(datasetId)
    localStorage.setItem('lastSelectedDatasetId', datasetId)
    // Reset symbol selection when dataset changes
    setSelectedSymbol('')
  }

  // Handle symbol change
  const handleSymbolChange = (symbol: string) => {
    setSelectedSymbol(symbol)
    // Save symbol selection to localStorage
    localStorage.setItem(`selectedSymbol_${selectedDatasetId}`, symbol)
  }

  // Safe navigation helper
  const navigateSafely = (navigationAction: () => void) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => navigationAction)
      setShowUnsavedDialog(true)
    } else {
      navigationAction()
    }
  }

  // Handle dialog confirmation
  const handleUnsavedDialogConfirm = () => {
    setShowUnsavedDialog(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  const handleUnsavedDialogCancel = () => {
    setShowUnsavedDialog(false)
    setPendingNavigation(null)
  }

  // Prevent browser tab close with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault()
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
        return 'You have unsaved changes. Are you sure you want to leave?'
      }
    }

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload)
      return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedChanges])

  // Handle undefined id (should default to 'new')
  const scriptId = id || 'new'
  const isNew = scriptId === 'new'
  const schema = isNew ? createScriptSchema : updateScriptSchema

  // Apply chat migrated via navigation state (save from new script) or from sessionStorage 'new'.
  // useLayoutEffect so it runs before paint and before AIScriptEditor reads sessionStorage.
  useLayoutEffect(() => {
    if (!scriptId || scriptId === 'new') return
    const keyScript = `quantquack-ai-chat-${scriptId}`
    try {
      const state = location.state as { migratedChat?: string } | null
      if (state?.migratedChat) {
        try {
          const parsed = JSON.parse(state.migratedChat)
          if (Array.isArray(parsed) && parsed.length > 0) {
            sessionStorage.setItem(keyScript, state.migratedChat)
          }
        } catch {
          // ignore
        }
        return
      }
      const keyNew = 'quantquack-ai-chat-new'
      const existing = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(keyScript) : null
      if (existing) return
      const rawNew = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(keyNew) : null
      if (!rawNew) return
      const parsed = JSON.parse(rawNew)
      if (!Array.isArray(parsed) || parsed.length === 0) return
      sessionStorage.setItem(keyScript, rawNew)
      sessionStorage.removeItem(keyNew)
    } catch {
      // ignore
    }
  }, [scriptId, location.state])

  // Restore unified chat when scriptId or migrated state changes
  useEffect(() => {
    const migrated = (location.state as { migratedChat?: string } | null)?.migratedChat
    if (migrated && scriptId !== 'new') {
      const parsed = parseChatFromJson(migrated)
      if (parsed?.length) {
        setUnifiedChatMessages(parsed)
        return
      }
    }
    const fromSession = loadChatFromSession(scriptId)
    setUnifiedChatMessages(fromSession ?? [])
  }, [scriptId])

  // Persist unified chat to sessionStorage
  useEffect(() => {
    saveChatToSession(scriptId, unifiedChatMessages)
  }, [scriptId, unifiedChatMessages])

  console.log('📄 ScriptEditorPage rendered:', { id, scriptId, isNew })

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CreateScriptInput | UpdateScriptInput>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      dslCode: '',
    },
  })

  // Reset state when creating a new script (or apply initial code from paper "Use code")
  useEffect(() => {
    if (isNew) {
      const state = location.state as { initialDslCode?: string; initialName?: string } | null
      const initialDslCode = state?.initialDslCode ?? ''
      const initialName = state?.initialName ?? ''
      console.log('🆕 Creating new script - resetting state', { isNew, scriptId, hasInitialCode: !!initialDslCode })
      // Reset store state for new script first
      useScriptStore.getState().setCurrentScript(null)
      // Reset local state
      setCurrentJobId(null)
      setResultsExpanded(true)
      setViewMode('table')
      setHasUnsavedChanges(!!(initialDslCode || initialName))
      setIsSavingAndRunning(false)
      // Reset form: use initial code/name from paper if provided, else empty
      setTimeout(() => {
        reset({
          name: initialName,
          dslCode: initialDslCode,
        })
      }, 0)
    } else {
      console.log('📝 Not a new script, isNew:', isNew, 'scriptId:', scriptId)
    }
  }, [isNew, reset, scriptId])

  // Open backtest config modal when navigating from configure page
  useEffect(() => {
    const state = location.state as { openBacktestConfig?: boolean } | null
    if (state?.openBacktestConfig && !isNew && currentScript) {
      setShowBacktestConfig(true)
      // Clear the state to prevent reopening on re-render
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, isNew, currentScript, navigate, location.pathname])

  // Keyboard shortcut for expanding/collapsing editor (Ctrl+E / Cmd+E)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'e') {
        event.preventDefault()
        setIsEditorExpanded(!isEditorExpanded)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isEditorExpanded])

  const name = watch('name')
  const dslCode = watch('dslCode')

  // When Create/Save is clicked but form validation fails, show modal (instead of silent failure or toast)
  const onSaveValidationError = (errors: Record<string, { message?: string } | undefined>) => {
    const firstMessage = Object.values(errors).find((e) => e?.message)?.message
    setValidationErrorModal({
      open: true,
      message: firstMessage || (isNew ? t('errors.nameRequired') : t('errors.saveScript')) || t('errors.saveScript'),
    })
  }

  // State to track if risk analysis mode is enabled
  const [riskAnalysisMode, setRiskAnalysisMode] = useState(false);

  // State to track if risk analysis is available (only when mode is enabled)
  const [riskAnalysisAvailable, setRiskAnalysisAvailable] = useState(false);

  // AI Configuration Modal
  const [aiConfigOpen, setAiConfigOpen] = useState(false);

  // Helper functions for panel management
  const expandBottomPanel = (targetPercent: number = 0.35) => {
    const minHeight = 36;
    const targetHeight = Math.max(minHeight + 1, Math.floor(window.innerHeight * targetPercent));
    setBottomPanelHeight(targetHeight);
    // Force editor layout update after panel resize
    setTimeout(() => {
      if (editorRef.current?.editor) {
        editorRef.current.editor.layout();
      }
    }, 200);
  };

  const collapseBottomPanel = (toMin: boolean = true) => {
    const minHeight = 36;
    if (toMin) {
      setBottomPanelHeight(minHeight);
    } else {
      setBottomPanelHeight(0);
      setActiveBottomTab(null);
    }
    // Force editor layout update after panel resize
    setTimeout(() => {
      if (editorRef.current?.editor) {
        editorRef.current.editor.layout();
      }
    }, 200);
  };

  const toggleBottomPanel = () => {
    const minHeight = 36;
    if (bottomPanelHeight === 0) {
      // Expand from hidden
      if (!activeBottomTab) {
        if (currentJobId) {
          setActiveBottomTab('results');
        } else if (riskAnalysisMode) {
          setActiveBottomTab('risk');
        }
      }
      expandBottomPanel(0.35);
    } else if (bottomPanelHeight <= minHeight) {
      // Expand from minimized (36px)
      expandBottomPanel(0.35);
      // Auto-select appropriate tab if none selected
      if (!activeBottomTab) {
        if (currentJobId) {
          setActiveBottomTab('results');
        } else if (riskAnalysisMode) {
          setActiveBottomTab('risk');
        }
      }
    } else {
      // Collapse to minimum (36px)
      collapseBottomPanel(true);
    }
  };

  // Handle risk analysis completion
  const handleRiskAnalysisComplete = (analysis: any) => {
    console.log('Risk analysis completed:', analysis);
    setRiskAnalysisAvailable(true);
    // Auto-expand panel if collapsed
    if (bottomPanelHeight <= 36) {
      expandBottomPanel(0.35);
    }
  };

  // Handle risk analysis error (feature not available yet)
  const handleRiskAnalysisError = () => {
    setRiskAnalysisAvailable(false);
  };

  // Send Risk & Signals to AI Assistant: open sidebar and trigger analysis (widget + AI response)
  const handleSendRiskToAIAssistant = (
    analysis: import('@/components/signals/SignalRiskWidget').SignalRiskAnalysis,
    context: { evaluationMode: 'hold' | 'day_close' | 'session_close'; simulationBars?: number }
  ): void => {
    if (sidebarCollapsed) setSidebarCollapsed(false)
    aiScriptEditorRef.current?.analyzeRiskInChat(analysis, context)
  }

  /** Quick date presets for Results toolbar (same logic as Execute modal). */
  const resultsDatePresets = [
    { id: 'all_time', name: t('results.presetAllTime', 'All time') },
    { id: 'today', name: t('results.presetToday', 'Today') },
    { id: 'yesterday', name: t('results.presetYesterday', 'Yesterday') },
    { id: 'last_5_days', name: t('results.presetLast5Days', 'Last 5 days') },
    { id: 'last_10_days', name: t('results.presetLast10Days', 'Last 10 days') },
    { id: 'this_month', name: t('results.presetThisMonth', 'This month') },
    { id: 'last_3_months', name: t('results.presetLast3Months', 'Last 3 months') },
    { id: 'last_1_year', name: t('results.presetLast1Year', 'Last 1 year') },
  ] as const

  const getResultsDateRangeFromPreset = (presetId: string): { startDate: string; endDate: string } => {
    if (presetId === 'all_time') return { startDate: '', endDate: '' }
    const now = new Date()
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    let start: Date
    let end: Date
    switch (presetId) {
      case 'today':
        start = end = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'yesterday':
        const y = new Date(now)
        y.setDate(y.getDate() - 1)
        start = end = new Date(y.getFullYear(), y.getMonth(), y.getDate())
        break
      case 'last_5_days':
        const d5 = new Date(now)
        d5.setDate(now.getDate() - 5)
        start = new Date(d5.getFullYear(), d5.getMonth(), d5.getDate())
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'last_10_days':
        const d10 = new Date(now)
        d10.setDate(now.getDate() - 10)
        start = new Date(d10.getFullYear(), d10.getMonth(), d10.getDate())
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'this_month':
        start = new Date(now.getFullYear(), now.getMonth(), 1)
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'last_3_months':
        const m3 = new Date(now)
        m3.setMonth(now.getMonth() - 3)
        start = new Date(m3.getFullYear(), m3.getMonth(), m3.getDate())
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'last_1_year':
        const y1 = new Date(now)
        y1.setFullYear(now.getFullYear() - 1)
        start = new Date(y1.getFullYear(), y1.getMonth(), y1.getDate())
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      default:
        return { startDate: '', endDate: '' }
    }
    return { startDate: fmt(start), endDate: fmt(end) }
  }

  /** Quick run from Results tab with current date range (and dataset/symbol). */
  const handleQuickRunAnalysis = async () => {
    if (hasUnsavedChanges) {
      showErrorToast(t('scripts.saveBeforeRun', 'Save the script before running'))
      return
    }
    const scriptId = currentScript?.id
    if (!scriptId || scriptId === 'new') {
      showErrorToast(t('scripts.saveBeforeRun', 'Save the script before running'))
      return
    }
    if (!selectedDatasetId) {
      showErrorToast(t('scripts.selectDatasetFirst', 'Select a dataset first'))
      return
    }
    try {
      const dateFilter =
        resultsDateStart || resultsDateEnd
          ? {
              startDate: resultsDateStart || undefined,
              endDate: resultsDateEnd || undefined,
            }
          : undefined
      const job = await createJob('execute', {
        scriptId,
        datasetId: selectedDatasetId,
        options: {
          outputFormat: 'json',
          executionMode: 'async',
          selectedSymbol: selectedSymbol || undefined,
          customInputs: selectedSymbol ? { selectedSymbol } : undefined,
          dateFilter,
        },
      })
      setCurrentJobId(job.id)
      setActiveBottomTab('results')
      setLastQuickRunParams({
        datasetId: selectedDatasetId,
        symbol: selectedSymbol || '',
        dateStart: resultsDateStart,
        dateEnd: resultsDateEnd,
      })
      showSuccessToast(t('jobs.queued', 'Job queued'))
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Failed to run')
    }
  }

  const quickRunParamsUnchanged = lastQuickRunParams != null &&
    lastQuickRunParams.datasetId === selectedDatasetId &&
    lastQuickRunParams.symbol === (selectedSymbol || '') &&
    lastQuickRunParams.dateStart === resultsDateStart &&
    lastQuickRunParams.dateEnd === resultsDateEnd

  // Render results content for bottom panel
  const renderResultsContent = () => {
    if (!job || !result) return null;

    if (job.status === 'queued' || job.status === 'processing') {
      return (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p className="text-sm">
              {job.status === 'queued' ? t('jobs.queued') : t('jobs.processing')}
            </p>
            {job.progress !== undefined && (
              <p className="text-xs mt-2">{job.progress}%</p>
            )}
          </div>
        </div>
      );
    }

    if (job.status === 'failed') {
      return (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded">
          <p className="text-sm text-destructive font-medium mb-1">{t('jobs.error')}</p>
          <p className="text-sm">{job.error || t('jobs.unknownError')}</p>
        </div>
      );
    }

    if (job.status === 'completed' && result) {
      return (
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="flex items-center justify-between border-b pb-2">
            <div className="flex items-center gap-2">
              {result.resultType === 'table' && Array.isArray(result.data) && (
                <>
                  <Button
                    variant={viewMode === 'table' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('table')}
                    className="h-7"
                  >
                    <Table className="w-3.5 h-3.5 mr-1.5" />
                    Table
                  </Button>
                  <Button
                    variant={viewMode === 'chart' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('chart')}
                    className="h-7"
                  >
                    <BarChart3 className="w-3.5 h-3.5 mr-1.5" />
                    Chart
                  </Button>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAIAnalysisOpen(true)}
                className="h-7"
              >
                <Brain className="w-3.5 h-3.5 mr-1.5" />
                {t('results.analyzeWithAI')}
              </Button>
              {Array.isArray(result.data) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (Array.isArray(result.data)) {
                      exportToCSV(result.data, `results-${currentJobId}-${Date.now()}.csv`);
                      showSuccessToast(t('results.exportCSVSuccess'));
                    }
                  }}
                  className="h-7"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  CSV
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  exportToJSON(result.data || result, `results-${currentJobId}-${Date.now()}.json`);
                  showSuccessToast(t('results.exportJSONSuccess'));
                }}
                className="h-7"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                JSON
              </Button>
            </div>
          </div>

          {/* Content - Always show table when we have array data */}
          {Array.isArray(result.data) && (() => {
            const cleanedData = cleanColumnNames(result.data);
            const rawStatsTypes = (result as { metadata?: { statsColumnTypes?: Record<string, string> } }).metadata?.statsColumnTypes;
            const statsColumnTypesClean: Record<string, string> = rawStatsTypes
              ? Object.fromEntries(Object.entries(rawStatsTypes).map(([k, v]) => [cleanColumnName(k), v]))
              : {};
            const groupColumns: string[] = result.resultType === 'stats'
              ? Object.entries(statsColumnTypesClean).filter(([, v]) => v === 'group').map(([k]) => k)
              : [];
            const filteredData = result.resultType === 'stats' && groupColumns.length > 0
              ? (cleanedData as Record<string, unknown>[]).filter((row) =>
                  groupColumns.every((col) => {
                    const filterVal = statsFilters[col];
                    if (filterVal == null || filterVal === '') return true;
                    const cell = row[col];
                    return String(cell) === String(filterVal);
                  })
                )
              : cleanedData;
            const dataForView = result.resultType === 'stats' ? filteredData : cleanedData;
            return (
              <div className="space-y-4">
                {/* Stats filters by group columns (session, ib_bias, etc.) */}
                {result.resultType === 'stats' && groupColumns.length > 0 && cleanedData.length > 0 && (
                  <div className="flex flex-wrap items-center gap-3 pb-2 border-b">
                    <Filter className="size-4 text-muted-foreground shrink-0" />
                    <span className="text-xs text-muted-foreground">Filter by:</span>
                    {groupColumns.map((col) => {
                      const values = [...new Set((cleanedData as Record<string, unknown>[]).map((r) => r[col]))].filter((v) => v != null).sort((a, b) => String(a).localeCompare(String(b)));
                      const label = col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                      return (
                        <div key={col} className="flex items-center gap-2">
                          <Label className="text-xs whitespace-nowrap">{label}</Label>
                          <Select
                            value={statsFilters[col] ?? '__all__'}
                            onValueChange={(v) => setStatsFilters((prev) => ({ ...prev, [col]: v === '__all__' ? null : v }))}
                          >
                            <SelectTrigger className="h-8 w-[120px] text-xs">
                              <SelectValue placeholder="All" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__all__" className="text-xs">All</SelectItem>
                              {values.map((v) => (
                                <SelectItem key={String(v)} value={String(v)} className="text-xs">
                                  {String(v)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })}
                    {(Object.values(statsFilters).some((v) => v != null && v !== '')) && (
                      <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setStatsFilters({})}>
                        Clear
                      </Button>
                    )}
                  </div>
                )}
                {/* Stats summary cards for stats resultType - aggregate across all rows */}
                {result.resultType === 'stats' && (dataForView.length > 0 ? (() => {
                  const rows = dataForView as Record<string, unknown>[];
                  const aggType = (key: string) => statsColumnTypesClean[key];
                  const isPercentLike = (key: string) =>
                    aggType(key) === 'percent' || (!rawStatsTypes && /pct|percent|accuracy|rate/i.test(key));
                  /** Use average when column is percent or avg; otherwise sum (counts, etc.) */
                  const useAverageForSummary = (key: string) => {
                    const t = aggType(key);
                    if (t === 'percent' || t === 'avg') return true;
                    if (!rawStatsTypes && /pct|percent|accuracy|rate/i.test(key)) return true;
                    return false;
                  };
                  const formatLabel = (key: string) =>
                    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                  const summary: Record<string, number | string> = { Rows: rows.length };
                  const uniqueSingleValueByKey: Record<string, string> = {};
                  groupColumns.forEach((col) => {
                    const values = [...new Set(rows.map((r) => r[col]))];
                    const uniqueCount = values.length;
                    const key = `Unique ${formatLabel(col)}`;
                    summary[key] = uniqueCount;
                    if (uniqueCount === 1 && values[0] != null) uniqueSingleValueByKey[key] = String(values[0]);
                  });
                  const numericKeys = Object.keys(rows[0]).filter((k) => typeof rows[0][k] === 'number');
                  numericKeys.forEach((key) => {
                    const nums = rows.map((r) => r[key] as number).filter((n) => typeof n === 'number');
                    if (nums.length === 0) return;
                    summary[key] = useAverageForSummary(key)
                      ? nums.reduce((a, b) => a + b, 0) / nums.length
                      : Math.round(nums.reduce((a, b) => a + b, 0));
                  });
                  const formatValue = (key: string, value: number | string) => {
                    if (typeof value !== 'number') return String(value);
                    if (key === 'Rows') return value.toLocaleString();
                    if (key.startsWith('Unique ')) {
                      const n = typeof value === 'number' ? value : Number(value);
                      if (n === 1 && uniqueSingleValueByKey[key])
                        return `1 (${uniqueSingleValueByKey[key]})`;
                      return typeof value === 'number' ? value.toLocaleString() : String(value);
                    }
                    return isPercentLike(key) ? `${value.toFixed(1)}%` : value.toLocaleString();
                  };
                  const getTooltip = (key: string) => {
                    const type = aggType(key);
                    if (key === 'Rows') return 'Number of rows in the result.';
                    if (key.startsWith('Unique ')) {
                      const label = key.replace(/^Unique /, '');
                      const suffix = uniqueSingleValueByKey[key] ? ` — only value: ${uniqueSingleValueByKey[key]}` : '';
                      return `Number of unique values for ${label} in the result.${suffix}`;
                    }
                    if (type === 'percent')
                      return `Average of "${formatLabel(key)}" across all rows (%).`;
                    if (type === 'avg')
                      return `Average of "${formatLabel(key)}" across all rows.`;
                    if (type === 'count')
                      return `Sum of "${formatLabel(key)}" across all rows.`;
                    if (type === 'max' || type === 'min' || type === 'stddev' || type === 'median')
                      return `${type === 'stddev' ? 'Std dev' : type.charAt(0).toUpperCase() + type.slice(1)} of "${formatLabel(key)}" across all rows.`;
                    if (isPercentLike(key))
                      return `Average of "${formatLabel(key)}" across all rows (%).`;
                    return `Sum of "${formatLabel(key)}" across all rows.`;
                  };
                  const isStructuralKey = (k: string) => k === 'Rows' || k.startsWith('Unique ');
                  const groupEntries = Object.entries(summary).filter(([k]) => isStructuralKey(k));
                  const countEntries = Object.entries(summary).filter(([k]) => !isStructuralKey(k) && !useAverageForSummary(k));
                  const pctEntries = Object.entries(summary).filter(([k]) => !isStructuralKey(k) && useAverageForSummary(k));
                  const maxCards = 18;
                  /** Show group first, then percents/avg (rates), then counts so percents are never pushed out */
                  const ordered = [...groupEntries, ...pctEntries, ...countEntries].slice(0, maxCards);
                  const StatIcon = ({ metricKey: k }: { metricKey: string }) => {
                    const t = aggType(k);
                    if (k === 'Rows' || k.startsWith('Unique ') || t === 'group') return <LayoutGrid className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />;
                    if (t === 'percent') return <Percent className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />;
                    if (t === 'avg') return <Divide className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />;
                    if (t === 'count' || t === 'max' || t === 'min' || t === 'stddev' || t === 'median') return <Sigma className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />;
                    if (isPercentLike(k)) return <Percent className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />;
                    return <Sigma className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />;
                  };
                  const StatCard = ({ metricKey: key, metricValue: value }: { metricKey: string; metricValue: number | string }) => (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="w-full text-left p-3 rounded-lg border bg-card hover:bg-muted/60 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        >
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1 min-w-0">
                            <StatIcon metricKey={key} />
                            <span className="truncate">{formatLabel(key)}</span>
                          </div>
                          <div className="font-semibold tabular-nums text-foreground">
                            {formatValue(key, value)}
                          </div>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-balance">
                        {getTooltip(key)}
                      </TooltipContent>
                    </Tooltip>
                  );
                  return (
                    <Card className="mb-4">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Summary statistics</CardTitle>
                        <p className="text-sm text-muted-foreground">
                          {dataForView.length === (cleanedData as unknown[]).length
                            ? `Aggregated across all ${rows.length} rows`
                            : `Filtered: ${rows.length} of ${(cleanedData as unknown[]).length} rows`}
                        </p>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <TooltipProvider delayDuration={300}>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                            {ordered.map(([key, value]) => (
                              <StatCard key={key} metricKey={key} metricValue={value} />
                            ))}
                          </div>
                        </TooltipProvider>
                      </CardContent>
                    </Card>
                  );
                })() : result.resultType === 'stats' && dataForView.length === 0 && (cleanedData as unknown[]).length > 0 ? (
                  <Card className="mb-4">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Summary statistics</CardTitle>
                      <p className="text-sm text-muted-foreground">No rows match the current filter.</p>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-xs text-muted-foreground">Clear filters or choose different values to see data.</p>
                    </CardContent>
                  </Card>
                ) : null)}

                {/* Table - Always shown for array data */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-muted-foreground">
                      📋 {result.rowCount ?? dataForView.length} rows
                      {result.resultType === 'signals' && ' (filtered by signals)'}
                      {result.resultType === 'stats' && ' (with statistics)'}
                      {result.resultType === 'table' && ' (raw data)'}
                    </div>
                    {(result.resultType === 'table' || result.resultType === 'stats') && (
                      <div className="flex gap-2">
                        <Button
                          variant={viewMode === 'table' ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => setViewMode('table')}
                          className="h-7"
                        >
                          <Table className="w-3.5 h-3.5 mr-1.5" />
                          Table
                        </Button>
                        <Button
                          variant={viewMode === 'chart' ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => setViewMode('chart')}
                          className="h-7"
                        >
                          <BarChart3 className="w-3.5 h-3.5 mr-1.5" />
                          Chart
                        </Button>
                      </div>
                    )}
                  </div>

                  {viewMode === 'table' || (result.resultType !== 'table' && result.resultType !== 'stats') ? (
                    <div className="space-y-2">
                      {dataForView.length > 500 && (
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-muted-foreground">
                            {showAllRows 
                              ? t('results.showingAllRows', { total: dataForView.length })
                              : t('results.showingRows', { count: 500, total: dataForView.length })
                            }
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowAllRows(!showAllRows)}
                            className="flex items-center gap-2 h-7 text-xs"
                          >
                            {showAllRows ? (
                              <>
                                <ChevronUp className="w-3 h-3" />
                                {t('results.hideRows', 'Hide Rows')}
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-3 h-3" />
                                {t('results.showAllRows', 'Show All Rows')}
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                      <div className="border rounded overflow-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-muted sticky top-0">
                            <tr>
                              {dataForView.length > 0 && Object.keys((dataForView[0] as Record<string, unknown>)).map((key) => (
                                <th key={key} className="px-3 py-1.5 text-left font-medium text-xs">{key}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(dataForView.length > 500 && !showAllRows 
                              ? dataForView.slice(0, 500) 
                              : dataForView
                            ).map((row: any, idx: number) => (
                              <tr key={idx} className="border-t hover:bg-muted/50">
                                {Object.values(row).map((value: any, colIdx: number) => (
                                  <td key={colIdx} className="px-3 py-1.5 font-mono text-xs">
                                    {typeof value === 'number' ? value.toLocaleString() : String(value)}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <ResultsChart
                      data={dataForView}
                      metadata={{ statsColumnTypes: statsColumnTypesClean }}
                      resultType={result.resultType}
                    />
                  )}
                </div>
              </div>
            );
          })()}

          {/* Fallback for non-array stats data */}
          {result.resultType === 'stats' && !Array.isArray(result.data) && result.data && typeof result.data === 'object' && (
            <div>
              <h4 className="text-sm font-medium mb-3">📊 Statistics</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(result.data as Record<string, unknown>).map(([key, value]) => (
                  <div key={key} className="p-3 bg-muted rounded text-sm">
                    <div className="text-xs text-muted-foreground mb-1">{key}</div>
                    <div className="font-medium">
                      {typeof value === 'number' ? value.toLocaleString() : String(value)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fallback for other non-array data */}
          {!Array.isArray(result.data) && result.data && (
            <pre className="text-xs bg-muted p-3 rounded overflow-auto font-mono">
              {JSON.stringify(result.data, null, 2)}
            </pre>
          )}
        </div>
      );
    }

    return null;
  };

  // Fetch current job status and result
  const { data: job, refetch: refetchJob, isLoading: isLoadingJob } = useQuery({
    queryKey: ['jobs', currentJobId],
    queryFn: async () => {
      const jobData = await jobsApi.get(currentJobId!)
      console.log('📥 Job fetched:', {
        id: jobData.id,
        status: jobData.status,
        resultId: jobData.resultId,
        progress: jobData.progress,
        hasError: !!jobData.error,
        errorLength: jobData.error?.length || 0
      })
      return jobData
    },
    enabled: !!currentJobId,
    refetchInterval: (query) => {
      const jobData = query.state.data
      // Poll every 2 seconds if job is active
      if (jobData?.status === 'queued' || jobData?.status === 'processing') {
        return 2000
      }
      // If completed or failed, stop polling
      if (jobData?.status === 'completed' || jobData?.status === 'failed') {
        return false
      }
      // Default: continue polling if status is unknown
      return 2000
    },
    // Refetch on window focus to catch updates
    refetchOnWindowFocus: true,
  })

  // Fetch result when job completes
  const { data: result } = useQuery({
    queryKey: ['results', job?.resultId || job?.id],
    queryFn: async () => {
      const resultId = job?.resultId || job?.id
      if (!resultId) return null
      const response = await apiClient.get(`/jobs/${resultId}/result`)
      return response.data
    },
    enabled: !!job && (job.status === 'completed' || !!job.resultId),
  })

  // Fetch signals via paginated API when job completes with signals result
  const { data: signalsResponse } = useQuery({
    queryKey: ['signals', job?.id, signalsPage],
    queryFn: async () => {
      if (!job?.id || result?.resultType !== 'signals') return null
      return await jobsApi.getSignals(job.id, 1, 1000) // Always fetch first page
    },
    enabled: !!job && job.status === 'completed' && result?.resultType === 'signals',
  })

  // Initialize signals data from API response
  useEffect(() => {
    if (signalsResponse?.success) {
      setSignalsData(signalsResponse.data)
      setSignalsColumns(signalsResponse.columns || [])
      setSignalsHasMore(signalsResponse.hasMore)
      setSignalsPage(1)
    } else if (result?.resultType !== 'signals') {
      // Reset signals state when result is not signals
      setSignalsData([])
      setSignalsColumns([])
      setSignalsHasMore(false)
      setSignalsPage(1)
    }
  }, [signalsResponse, result?.resultType])

  // Function to load more signals
  const loadMoreSignals = async () => {
    if (!job?.id || isLoadingMoreSignals || !signalsHasMore) return

    setIsLoadingMoreSignals(true)
    try {
      const nextPage = signalsPage + 1
      const response = await jobsApi.getSignals(job.id, nextPage, 1000)
      
      if (response.success && response.data.length > 0) {
        setSignalsData(prev => [...prev, ...response.data])
        setSignalsPage(nextPage)
        setSignalsHasMore(response.hasMore)
        if (response.columns.length > 0 && signalsColumns.length === 0) {
          setSignalsColumns(response.columns)
        }
      } else {
        setSignalsHasMore(false)
      }
    } catch (error) {
      console.error('Failed to load more signals:', error)
      showErrorToast(error as Error, 'Failed to load more signals')
      setSignalsHasMore(false)
    } finally {
      setIsLoadingMoreSignals(false)
    }
  }

  // Fetch dataset information for AI analysis context
  const { data: dataset } = useQuery({
    queryKey: ['datasets', selectedDatasetId],
    queryFn: async () => {
      if (!selectedDatasetId) return null
      return await datasetsApi.get(selectedDatasetId)
    },
    enabled: !!selectedDatasetId,
  })

  // Reset stats filters when job/result changes
  useEffect(() => {
    setStatsFilters({});
  }, [currentJobId]);

  // Check if data is too large for AI analysis
  const dataSizeCheck = useMemo(() => {
    if (!result?.data) return { tooLarge: false };
    return isDataTooLargeForAI(result.data);
  }, [result?.data]);

  // Handle job completion - track previous status to detect transitions
  const previousStatusRef = useRef<string | undefined>(undefined)
  
  useEffect(() => {
    if (!job) return
    
    const previousStatus = previousStatusRef.current
    const currentStatus = job.status
    
    console.log('🔄 Job status changed:', { 
      previousStatus, 
      currentStatus, 
      resultId: job.resultId,
      progress: job.progress 
    })
    
    // Update ref for next comparison
    previousStatusRef.current = currentStatus
    
    // Show toast when job transitions to completed
    if (
      previousStatus &&
      (previousStatus === 'queued' || previousStatus === 'processing') &&
      currentStatus === 'completed'
    ) {
      console.log('✅ Job completed! Refetching...')
      showSuccessToast(t('jobs.completedSuccess'))
      // Refetch to get latest data including resultId
      setTimeout(() => {
        refetchJob()
      }, 500)
      
      // Clear markers on success
      if (editorRef.current) {
        const { editor, monaco } = editorRef.current
        monaco.editor.setModelMarkers(editor.getModel(), 'compilation', [])
      }
    }
    
    // Show error toast when job fails
    if (
      previousStatus &&
      (previousStatus === 'queued' || previousStatus === 'processing') &&
      currentStatus === 'failed'
    ) {
      console.log('❌ Job failed! Processing error display...')
      console.log('🔍 Job error details:', {
        hasError: !!job.error,
        errorLength: job.error?.length || 0,
        errorPreview: job.error?.substring(0, 100) || 'none',
        jobStatus: job.status,
        currentJobId,
        activeBottomTab
      });

      showErrorToast(new Error(job.error || 'Job failed'))

      // Show error in results tab
      console.log('🔄 Activating results tab for error display');
      setActiveBottomTab('results');

      // Auto-expand panel if collapsed to show error
      console.log('📏 Current panel state:', {
        bottomPanelHeight,
        activeBottomTab: 'checking...',
        shouldExpand: bottomPanelHeight <= 36
      });

      if (bottomPanelHeight <= 36) {
        console.log('📈 Auto-expanding panel to show error');
        setBottomPanelHeight(400); // Default expanded height
      } else {
        console.log('✅ Panel already expanded, no need to expand');
      }

      // Parse error message and set markers in Monaco if it's a compilation error
      if (job.error && editorRef.current) {
        const errorMessage = job.error
        // Check if error message contains compilation error format: "Line X, column Y: ..."
        const lineMatch = errorMessage.match(/Line\s+(\d+)(?:,\s*column\s+(\d+))?:\s*(.+)/i)
        
        if (lineMatch) {
          const line = parseInt(lineMatch[1], 10)
          const column = lineMatch[2] ? parseInt(lineMatch[2], 10) : 1
          const message = lineMatch[3] || errorMessage
          
          const { editor, monaco } = editorRef.current
          const markers = [{
            severity: monaco.MarkerSeverity.Error,
            startLineNumber: line,
            startColumn: column,
            endLineNumber: line,
            endColumn: column + 1,
            message: message,
          }]
          
          monaco.editor.setModelMarkers(editor.getModel(), 'compilation', markers)
          
          // Scroll to error
          editor.revealLineInCenter(line)
          editor.setPosition({ lineNumber: line, column: column })
        } else {
          // If no line info, clear markers (might be a different type of error)
          const { editor, monaco } = editorRef.current
          monaco.editor.setModelMarkers(editor.getModel(), 'compilation', [])
        }
      }
    }
  }, [job?.status, job?.resultId, job?.error, refetchJob, t])

  // Auto-open bottom panel when results or risk analysis is available
  // Note: This only applies to non-backtest jobs (execute/compile jobs)
  useEffect(() => {
    if (currentJobId && currentJobId !== backtestJobId) {
      // Only auto-switch to results for non-backtest jobs
      if (activeBottomTab !== 'backtests') {
        setActiveBottomTab('results');
      }
      // Auto-expand if collapsed or minimized
      if (bottomPanelHeight <= 36) {
        expandBottomPanel(0.35);
      }
    }
  }, [currentJobId, backtestJobId, activeBottomTab]);

  useEffect(() => {
    if (riskAnalysisMode) {
      setActiveBottomTab('risk');
      // Auto-expand if collapsed or minimized
      if (bottomPanelHeight <= 36) {
        expandBottomPanel(0.35);
      }
    }
  }, [riskAnalysisMode]);

  // Ensure backtests tab is active when backtestJobId is set
  useEffect(() => {
    if (backtestJobId && !isNew && currentScript?.id) {
      setResultsExpanded(true);
      setActiveBottomTab('backtests');
      // Always expand panel when backtestJobId is set (even if hidden)
      expandBottomPanel(0.4);
    }
  }, [backtestJobId, isNew, currentScript?.id]);

  // Force editor layout update when panel height changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (editorRef.current?.editor) {
        editorRef.current.editor.layout();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [bottomPanelHeight, headerCollapsed, sidebarCollapsed]);

  useEffect(() => {
    console.log('🔄 useEffect for loadScript:', { id, scriptId, isNew })
    if (scriptId && scriptId !== 'new') {
      console.log('📥 Loading script:', scriptId)
      loadScript(scriptId)
    }
  }, [scriptId, loadScript])

  useEffect(() => {
    if (currentScript && !isNew) {
      console.log('📝 Loading existing script values:', currentScript.name)
      setValue('name', currentScript.name)
      setValue('dslCode', currentScript.dslCode)
      if (currentScript.selectedDatasetId) {
        setSelectedDatasetId(currentScript.selectedDatasetId)
        // Load symbol selection for this dataset
        const savedSymbol = localStorage.getItem(`selectedSymbol_${currentScript.selectedDatasetId}`)
        if (savedSymbol) {
          setSelectedSymbol(savedSymbol)
        }
      }
      setHasUnsavedChanges(false) // Reset when script loads
    }
  }, [currentScript, setValue, isNew])

  // Detect unsaved changes
  useEffect(() => {
    if (!currentScript) {
      // New script: has changes if name or code is not empty
      setHasUnsavedChanges(!!((name || '').trim() || (dslCode || '').trim()))
      return
    }

    // Existing script: check if name or code changed
    const nameChanged = (name || '').trim() !== (currentScript.name || '').trim()
    const codeChanged = (dslCode || '').trim() !== (currentScript.dslCode || '').trim()
    setHasUnsavedChanges(nameChanged || codeChanged)
  }, [name, dslCode, currentScript])

  const onSubmit = async (data: CreateScriptInput | UpdateScriptInput) => {
    
    // Use scriptId instead of id (handles undefined case)
    const currentId = scriptId
    
    // Use current form values (including from Monaco editor)
    // Prefer watched values as they're always up-to-date
    const scriptName = (name || data.name || t('scripts.untitled')).trim()
    const scriptCode = (dslCode || data.dslCode || '').trim()
    
    // If saving with unsaved code changes, add to AI panel version history (or store pending if panel is closed)
    if (!isNew && currentScript && scriptCode && (scriptCode !== (currentScript.dslCode || '').trim())) {
      if (aiScriptEditorRef.current) {
        aiScriptEditorRef.current.createVersion(scriptCode, 'Saved')
      } else {
        setPendingSavedVersion({ code: scriptCode, description: 'Saved' })
      }
    }
    
    console.log('📝 Script data prepared:', { scriptName, scriptCodeLength: scriptCode.length })
    
    // Validate required fields
    if (!scriptName) {
      console.log('❌ Script name is empty')
      showErrorToast(new Error(t('errors.nameRequired') || 'Script name is required'), t('errors.saveScript'))
      return
    }
    
    if (!scriptCode) {
      console.log('❌ DSL code is empty')
      showErrorToast(new Error(t('errors.dslCodeRequired') || 'DSL code is required'), t('errors.saveScript'))
      return
    }
    
    console.log('✅ Validation passed, starting save...')
    setIsSaving(true)
    
    try {
      if (isNew) {
        const result = await createScript(scriptName, scriptCode, selectedDatasetId || undefined)
        
        // Check for compilation errors (result has script and compilation)
        if (result.compilation && !result.compilation.success && result.compilation.errors) {
          const errorMessages = result.compilation.errors.map((e: any) => {
            const baseMessage = e.message || `${e.type}: ${e.message || 'Unknown error'}`
            if (e.line !== undefined) {
              return `Line ${e.line}${e.column !== undefined ? `, column ${e.column}` : ''}: ${baseMessage}`
            }
            return baseMessage
          }).join('\n')
          
          // Set markers in Monaco editor
          if (editorRef.current) {
            const { editor, monaco } = editorRef.current
            const markers = result.compilation.errors.map((e: any) => ({
              severity: monaco.MarkerSeverity.Error,
              startLineNumber: e.line || 1,
              startColumn: e.column || 1,
              endLineNumber: e.line || 1,
              endColumn: e.column || 999,
              message: e.message || `${e.type}: ${e.message || 'Unknown error'}`,
            }))
            monaco.editor.setModelMarkers(editor.getModel(), 'compilation', markers)
            
            // Scroll to first error
            if (markers.length > 0) {
              editor.revealLineInCenter(markers[0].startLineNumber)
              editor.setPosition({ lineNumber: markers[0].startLineNumber, column: markers[0].startColumn })
            }
          }
          
          console.log('🚨 Compilation error:', errorMessages)
          showErrorToast(new Error(errorMessages), t('scripts.compilationFailed'))
        } else {
          // Clear markers on success
          if (editorRef.current) {
            const { editor, monaco } = editorRef.current
            monaco.editor.setModelMarkers(editor.getModel(), 'compilation', [])
          }
          
          if (result.compilation?.warnings && result.compilation.warnings.length > 0) {
            showSuccessToast(t('scripts.saveSuccess') + ` (${result.compilation.warnings.length} warnings)`)
          } else {
            showSuccessToast(t('scripts.createSuccess'))
          }
        }
        
        const script = result.script || result
        // Pass chat from 'new' in state so the script page has it even if sessionStorage is cleared or timing differs
        const chatRaw =
          typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('quantquack-ai-chat-new') : null
        useScriptStore.getState().setCurrentScript(script)
        navigate(`/scripts/${script.id}`, {
          replace: true,
          state: chatRaw ? { migratedChat: chatRaw } : undefined,
        })
      } else {
        const result = await updateScript(currentId, scriptName, scriptCode, selectedDatasetId || null)
        
        // Check for compilation errors (result has script and compilation)
        if (result.compilation && !result.compilation.success && result.compilation.errors) {
          const errorMessages = result.compilation.errors.map((e: any) => {
            const baseMessage = e.message || `${e.type}: ${e.message || 'Unknown error'}`
            if (e.line !== undefined) {
              return `Line ${e.line}${e.column !== undefined ? `, column ${e.column}` : ''}: ${baseMessage}`
            }
            return baseMessage
          }).join('\n')
          
          // Set markers in Monaco editor
          if (editorRef.current) {
            const { editor, monaco } = editorRef.current
            const markers = result.compilation.errors.map((e: any) => ({
              severity: monaco.MarkerSeverity.Error,
              startLineNumber: e.line || 1,
              startColumn: e.column || 1,
              endLineNumber: e.line || 1,
              endColumn: e.column || 999,
              message: e.message || `${e.type}: ${e.message || 'Unknown error'}`,
            }))
            monaco.editor.setModelMarkers(editor.getModel(), 'compilation', markers)
            
            // Scroll to first error
            if (markers.length > 0) {
              editor.revealLineInCenter(markers[0].startLineNumber)
              editor.setPosition({ lineNumber: markers[0].startLineNumber, column: markers[0].startColumn })
            }
          }
          
          console.log('🚨 Compilation error:', errorMessages)
          showErrorToast(new Error(errorMessages), t('scripts.compilationFailed'))
        } else {
          // Clear markers on success
          if (editorRef.current) {
            const { editor, monaco } = editorRef.current
            monaco.editor.setModelMarkers(editor.getModel(), 'compilation', [])
          }
          
          if (result.compilation?.warnings && result.compilation.warnings.length > 0) {
            showSuccessToast(t('scripts.saveSuccess') + ` (${result.compilation.warnings.length} warnings)`)
          } else {
            showSuccessToast(t('scripts.saveSuccess'))
          }
        }
      }
    } catch (error) {
      console.error('❌ Error saving script:', error)
      showErrorToast(error, t('errors.saveScript'))
    } finally {
      setIsSaving(false)
    }
  }

  // Fetch datasets for selector
  const { data: datasetsData } = useQuery({
    queryKey: ['datasets'],
    queryFn: async () => {
      const result = await datasetsApi.list()
      return result.datasets
    },
  })

  // Fetch dataset preview when selected and preview is enabled
  const { data: datasetPreview, isLoading: isLoadingPreview } = useQuery({
    queryKey: ['dataset-preview', selectedDatasetId],
    queryFn: () => datasetsApi.preview(selectedDatasetId),
    enabled: !!selectedDatasetId && showDatasetPreview,
  })

  const readyDatasets = datasetsData?.filter((d) => d.status === 'ready') || []


  // Show loading only when initially loading an existing script, not during save operations
  if (isLoading && !isNew && !currentScript) {
    return <LoadingPage message={t('common.loading')} />
  }

  // Show error if script doesn't exist (only for existing scripts, not new ones)
  if (!isLoading && !isNew && !currentScript) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-lg text-muted-foreground">{t('errors.scriptNotFound')}</p>
          <Button onClick={() => navigate('/scripts')} className="mt-4">
            {t('common.back')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden bg-background">
      {/* Cursor-style Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Activity Bar (Cursor style) */}
        <ActivityBar onNavigateBack={() => navigateSafely(() => navigate('/scripts'))} />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden" style={{ flex: 1, minHeight: 0 }}>
          <div className="flex-shrink-0">
          <TitleBar
            scriptName={name || ''}
            onNameChange={(newName) => setValue('name', newName, { shouldDirty: true })}
            isCompiled={!!currentScript?.compiledSql}
            hasUnsavedChanges={hasUnsavedChanges}
            isSaving={isSaving}
            isSavingAndRunning={isSavingAndRunning}
            isLoading={isLoading}
            isNew={isNew}
            scriptId={scriptId}
            selectedDatasetId={selectedDatasetId}
            onDatasetChange={handleDatasetChange}
            datasets={readyDatasets}
            selectedSymbol={selectedSymbol}
            onSymbolChange={handleSymbolChange}
            currentMetadata={(currentScript?.metadata as DatasetMetadata | null) || null}
            onMetadataUpdated={() => loadScript(scriptId)}
            onSave={() => handleSubmit(onSubmit, onSaveValidationError)()}
            onSaveBeforeRun={async () => {
              setIsSavingAndRunning(true)
              try {
                const scriptName = ((name || '') || t('scripts.untitled')).trim()
                const scriptCode = (dslCode || '').trim()
                
                if (!scriptName || !scriptCode) {
                  throw new Error('Script name and code are required')
                }
                
                let savedScriptId: string
                
                if (isNew) {
                  const result = await createScript(scriptName, scriptCode, selectedDatasetId || undefined)
                  const script = (result as any).script || result
                  savedScriptId = script.id
                  const chatRaw =
                    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('quantquack-ai-chat-new') : null
                  useScriptStore.getState().setCurrentScript(script)
                  navigate(`/scripts/${savedScriptId}`, {
                    replace: true,
                    state: chatRaw ? { migratedChat: chatRaw } : undefined,
                  })
                } else {
                  await updateScript(scriptId, scriptName, scriptCode, selectedDatasetId || null)
                  await loadScript(scriptId)
                  const updatedScript = useScriptStore.getState().currentScript
                  if (!updatedScript || !updatedScript.id) {
                    throw new Error('Failed to get script ID after save')
                  }
                  savedScriptId = updatedScript.id
                }
                
                await new Promise(resolve => setTimeout(resolve, 200))
                return savedScriptId
              } catch (error) {
                console.error('Failed to save before run:', error)
                showErrorToast(error, t('errors.saveScript'))
                throw error
              } finally {
                setIsSavingAndRunning(false)
              }
            }}
            onSaveName={async (newName: string) => {
              if (isNew) {
                // New script: only update local form state (no API; script not created yet)
                setValue('name', newName, { shouldDirty: false })
                return
              }
              try {
                // Only update the name, don't save the entire script
                await updateScript(scriptId, newName, dslCode || '', selectedDatasetId || null)
                setValue('name', newName, { shouldDirty: false })
                showSuccessToast('Script name updated successfully')
              } catch (error) {
                console.error('Failed to update script name:', error)
                showErrorToast(error, 'Failed to update script name')
                throw error
              }
            }}
            dslCode={dslCode || ''}
            dateStart={resultsDateStart}
            dateEnd={resultsDateEnd}
            datePreset={resultsDatePreset}
            onDateStartChange={(v) => { setResultsDateStart(v); setResultsDatePreset('') }}
            onDateEndChange={(v) => { setResultsDateEnd(v); setResultsDatePreset('') }}
            onDatePresetChange={(presetId) => {
              setResultsDatePreset(presetId)
              const r = getResultsDateRangeFromPreset(presetId)
              setResultsDateStart(r.startDate)
              setResultsDateEnd(r.endDate)
            }}
            datePresets={resultsDatePresets}
            headerCollapsed={headerCollapsed}
            onToggleHeader={() => setHeaderCollapsed(!headerCollapsed)}
            editor={editorRef.current?.editor}
            sidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
            onBacktestConfigClick={() => setShowBacktestConfig(true)}
            onBacktestRun={(jobId) => {
              setBacktestJobId(jobId);
              // Don't set currentJobId for backtests to avoid interfering with results tab
              setResultsExpanded(true);
              setHasUnsavedChanges(false);
              setActiveBottomTab('backtests');
              // Always expand panel when running backtest (even if hidden)
              expandBottomPanel(0.4);
            }}
            onSaveBeforeBacktest={async (): Promise<string> => {
              setIsSavingAndRunning(true);
              try {
                const scriptName = ((name || '') || t('scripts.untitled')).trim();
                const scriptCode = (dslCode || '').trim();
                
                if (!scriptName || !scriptCode) {
                  throw new Error('Script name and code are required');
                }
                
                let savedScriptId: string;
                
                if (isNew) {
                  const result = await createScript(scriptName, scriptCode, selectedDatasetId || undefined);
                  const script = (result as any).script || result;
                  savedScriptId = script.id;
                  const chatRaw =
                    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('quantquack-ai-chat-new') : null;
                  useScriptStore.getState().setCurrentScript(script);
                  navigate(`/scripts/${savedScriptId}`, {
                    replace: true,
                    state: chatRaw ? { migratedChat: chatRaw } : undefined,
                  });
                } else {
                  await updateScript(scriptId, scriptName, scriptCode, selectedDatasetId || null);
                  savedScriptId = scriptId;
                }
                
                await new Promise(resolve => setTimeout(resolve, 200));
                return savedScriptId;
              } catch (error) {
                console.error('Failed to save before backtest:', error);
                showErrorToast(error, t('errors.saveScript'));
                throw error;
              } finally {
                setIsSavingAndRunning(false);
                setResultsExpanded(true);
              }
            }}
            onJobCreated={(jobId) => {
              setCurrentJobId(jobId)
              setResultsExpanded(true)
              setHasUnsavedChanges(false)
              setActiveBottomTab('results')
              if (bottomPanelHeight <= 36) {
                expandBottomPanel(0.4)
              }
            }}
          />
          </div>

          {/* Editor, Sidebar and Bottom Panel Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Editor and Sidebar Row */}
            <div className="flex-1 flex overflow-hidden">
              {/* Editor */}
              <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                  <MonacoEditor
                    value={dslCode || ''}
                    onChange={(value) => {
                      setValue('dslCode', value || '', { shouldValidate: true, shouldDirty: true })
                    }}
                    language="quantquack-dsl"
                    height="100%"
                    onMount={(editor, monaco) => {
                      editorRef.current = { editor, monaco }
                      // Force layout update when editor mounts
                      setTimeout(() => {
                        editor.layout()
                      }, 100)
                    }}
                  />
                {errors.dslCode && (
                  <div className="px-3 py-1.5 bg-destructive/10 border-t border-destructive/20 text-xs">
                    <p className="text-destructive">{errors.dslCode.message}</p>
          </div>
                  )}
                </div>

              {/* Right Sidebar - AI Tools - Full Height */}
              {!sidebarCollapsed && (
                <>
                  <div
                    className="flex-1 flex flex-col border-l bg-muted/30 overflow-hidden transition-all duration-200 ease-in-out"
                    style={{ width: `${sidebarWidth}px`, minWidth: '200px', maxWidth: '600px' }}
                  >
                    <div className="flex-1 overflow-y-auto p-3 space-y-3">
                    {/* Sidebar Header with Collapse Button */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tools</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSidebarCollapsed(true)}
                        className="h-6 w-6"
                        title="Collapse sidebar"
                      >
                        <ChevronDown className="w-3 h-3 rotate-90" />
                      </Button>
                    </div>
                    
                    {/* Compilation Status */}
                    <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-background border text-xs">
                      <div className={`w-1.5 h-1.5 rounded-full ${currentScript?.compiledSql ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                      <span className="font-medium">
                  {currentScript?.compiledSql
                    ? t('scripts.compiledSuccessfully')
                    : t('scripts.notCompiled')}
                      </span>
              </div>

                    {/* Risk Analysis Toggle */}
                    {isValidForSignalAnalysis(dslCode || "") && !!selectedDatasetId && (
                      <div className="flex items-center justify-between px-2 py-1.5 rounded bg-background border">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-muted-foreground" />
                          <Label htmlFor="risk-analysis-toggle" className="text-xs font-medium cursor-pointer">
                            Risk & Signals
                          </Label>
                          {riskAnalysisMode && !riskAnalysisAvailable && (
                            <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                          )}
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            id="risk-analysis-toggle"
                            type="checkbox"
                            checked={riskAnalysisMode}
                            onChange={(e) => {
                              const newMode = e.target.checked;
                              setRiskAnalysisMode(newMode);
                              if (newMode) {
                                setActiveBottomTab('risk');
                                // Auto-expand if collapsed or minimized
                                if (bottomPanelHeight <= 36) {
                                  expandBottomPanel(0.35);
                                }
                              } else {
                                setRiskAnalysisAvailable(false);
                              }
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ring peer-focus:ring-offset-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                        </label>
        </div>
        )}

                    {/* AI Script Generation - Only for new scripts without code; hide when AI Assistant (Chat) is shown */}
                    {isNew && !(dslCode && dslCode.trim()) && (
                      <AIScriptGenerator
                        chatMessages={unifiedChatMessages}
                        setChatMessages={setUnifiedChatMessages}
                        onCodeGenerated={(code) => {
                          setValue('dslCode', code, { shouldValidate: true, shouldDirty: true });
                          showSuccessToast('Script generated and applied to editor');
                        }}
                        existingSignals={extractSignalNames(dslCode || '')}
                        datasetInfo={(() => {
                          const selectedDataset = readyDatasets.find(d => d.id === selectedDatasetId);
                          if (!selectedDataset) return undefined;
                          return {
                            symbol: selectedDataset.metadata?.symbol,
                            timeframe: typeof selectedDataset.metadata?.timeframe === 'string' 
                              ? selectedDataset.metadata.timeframe 
                              : undefined,
                            columns: Array.isArray(selectedDataset.metadata?.columns) 
                              ? selectedDataset.metadata.columns.filter((c): c is string => typeof c === 'string')
                              : undefined
                          };
                        })()}
                        onConfigureClick={() => setAiConfigOpen(true)}
                      />
                    )}

                    {/* Dataset Preview */}
                    {selectedDatasetId && (
                      <div className="flex items-center justify-between px-2 py-1.5 rounded bg-background border">
                        <div className="flex items-center gap-2">
                          <Database className="w-4 h-4 text-muted-foreground" />
                          <Label htmlFor="dataset-preview-toggle" className="text-xs font-medium cursor-pointer">
                            Dataset Preview
                          </Label>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            id="dataset-preview-toggle"
                            type="checkbox"
                            checked={showDatasetPreview}
                            onChange={(e) => setShowDatasetPreview(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ring peer-focus:ring-offset-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                      </div>
                    )}

                    {/* Dataset Preview Chart */}
                    {showDatasetPreview && selectedDatasetId && datasetPreview && (
                      <DatasetPreviewChart
                        data={datasetPreview.rows}
                        columns={datasetPreview.columns}
                        isLoading={isLoadingPreview}
                        title="Dataset Preview"
                        isMultiSymbol={dataset?.metadata?.partitionStrategy === 'by_symbol' ||
                                        dataset?.metadata?.partitionType === 'symbol'}
                        availableSymbols={((dataset?.metadata as any)?.symbols as string[]) ||
                                        ((dataset?.metadata as any)?.syncMetadata?.symbols as string[]) || []}
                        selectedSymbol={selectedSymbol}
                        onSymbolChange={handleSymbolChange}
                      />
                    )}

                    {/* AI Script Editor - for existing scripts and for new ones (chat under 'new' until save, then migrates to script id) */}
                    {/* Use route scriptId so after save+navigate we have the new id immediately and chat migrates from 'new' */}
                    {dslCode && dslCode.trim() && (
                      <AIScriptEditor
                        ref={aiScriptEditorRef}
                        currentScript={dslCode}
                        scriptId={scriptId}
                        migratedChatFromState={(location.state as { migratedChat?: string } | null)?.migratedChat ?? undefined}
                        unifiedChatMessages={unifiedChatMessages}
                        setUnifiedChatMessages={setUnifiedChatMessages}
                        runAndAnalyzeParams={
                          scriptId && scriptId !== 'new' && selectedDatasetId
                            ? {
                                scriptId,
                                datasetId: selectedDatasetId,
                                symbol: selectedSymbol || undefined,
                                dateStart: resultsDateStart,
                                dateEnd: resultsDateEnd,
                                scriptCode: dslCode || '',
                                scriptDescription: currentScript?.name,
                                datasetInfo: (() => {
                                  const d = readyDatasets.find((x) => x.id === selectedDatasetId);
                                  const meta = d?.metadata as Record<string, unknown> | undefined;
                                  if (!d) return undefined;
                                  return {
                                    symbol: (meta?.symbol ?? d.symbol) as string | undefined,
                                    timeframe: (meta?.timeframe ?? meta?.interval ?? d.timeframe) as string | undefined,
                                    columns: (meta?.columns as string[] | undefined)?.map((c) => (typeof c === 'string' ? c : (c as any)?.name ?? '')).filter(Boolean),
                                    timezone: (meta?.timezone ?? d.timezone) as string | undefined,
                                    sessionStrategy: meta?.session_strategy as 'by_date' | 'custom' | 'trading_sessions' | undefined,
                                    sessions: meta?.sessions as Array<{ name: string; start_time: string; end_time: string; description?: string }> | undefined,
                                    sessionIncludeDate: meta?.session_include_date as boolean | undefined,
                                    intervalDuration: meta?.interval_duration as number | string | undefined,
                                    intervalIdFormat: meta?.interval_id_format as 'timestamp' | 'datetime' | undefined,
                                  };
                                })(),
                              }
                            : null
                        }
                        pendingSavedVersion={pendingSavedVersion}
                        onConsumePendingSavedVersion={() => setPendingSavedVersion(null)}
                        onScriptUpdated={(updatedCode) => {
                          setValue('dslCode', updatedCode, { shouldValidate: true, shouldDirty: true });
                          // Editor updates from controlled value; avoid double-update (setValue + ref) to prevent Monaco dispose "Canceled"
                          requestAnimationFrame(() => {
                            editorRef.current?.editor?.getAction('editor.action.formatDocument')?.run();
                          });
                        }}
                        onConfigureClick={() => setAiConfigOpen(true)}
                        existingSignals={extractSignalNames(dslCode || '')}
                        datasetInfo={(() => {
                          const selectedDataset = readyDatasets.find(d => d.id === selectedDatasetId);
                          if (!selectedDataset) return undefined;
                          return {
                            symbol: selectedDataset.metadata?.symbol,
                            timeframe: typeof selectedDataset.metadata?.interval === 'string'
                              ? selectedDataset.metadata.interval
                              : undefined,
                            columns: selectedDataset.metadata?.columns?.map(c =>
                              typeof c === 'string' ? c : (c as any)?.name || ''
                            ).filter(Boolean) as string[] | undefined
                          };
                        })()}
                        compilationInfo={undefined}
                      />
        )}
                    </div>
                  </div>
                  {/* Sidebar Resize Handle */}
                  <div
                    className="w-1 cursor-col-resize hover:bg-primary/20 transition-colors bg-border flex-shrink-0"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const startX = e.clientX;
                      const startWidth = sidebarWidth;
                      
                      const handleMouseMove = (moveEvent: MouseEvent) => {
                        const deltaX = moveEvent.clientX - startX;
                        const newWidth = Math.max(200, Math.min(600, startWidth + deltaX));
                        setSidebarWidth(newWidth);
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                        document.body.style.cursor = '';
                        document.body.style.userSelect = '';
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                      document.body.style.cursor = 'col-resize';
                      document.body.style.userSelect = 'none';
                    }}
                  />
                </>
              )}
              
              {/* Collapsed Sidebar Toggle */}
              {sidebarCollapsed && (
                <div className="w-8 flex items-center border-l bg-muted/30 transition-all duration-200 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSidebarCollapsed(false)}
                    className="h-full w-full rounded-none"
                    title="Expand sidebar"
                  >
                    <ChevronDown className="w-4 h-4 -rotate-90" />
                  </Button>
                </div>
              )}
            </div>

            {/* Bottom Panel - Cursor style - Same level as Editor/Sidebar */}
          {(() => {
            // Only show panel if there's content and height > 0
            const hasContent = currentJobId || riskAnalysisMode || backtestJobId;
            const shouldShow = hasContent && bottomPanelHeight > 0;
            return shouldShow && (
            <div
              className="border-t bg-background flex flex-col transition-all duration-200 ease-in-out overflow-hidden relative"
              style={{
                height: `${bottomPanelHeight}px`,
                flexShrink: 0,
                flexGrow: 0,
                flexBasis: `${bottomPanelHeight}px`
              }}
            >
            {/* Resize Handle - Top border */}
            <div
              className="absolute top-0 left-0 right-0 h-1.5 cursor-row-resize hover:h-2 hover:bg-primary/40 transition-all bg-border/50 z-10 group"
              onMouseDown={(e) => {
                e.preventDefault();
                const startY = e.clientY;
                const startHeight = bottomPanelHeight;
                
                const handleMouseMove = (moveEvent: MouseEvent) => {
                  const deltaY = startY - moveEvent.clientY; // Inverted: dragging up increases height
                  // Allow expansion up to 80% of viewport height
                  const maxHeight = Math.floor(window.innerHeight * 0.8);
                  const minHeight = 36; // Height of tab bar (h-9 = 36px)
                  const newHeight = Math.max(minHeight, Math.min(maxHeight, startHeight + deltaY));
                  setBottomPanelHeight(newHeight);
                  // Auto-select a tab if panel is expanded and no tab is selected
                  if (newHeight > minHeight && !activeBottomTab) {
                    if (currentJobId) {
                      setActiveBottomTab('results');
                    } else if (riskAnalysisMode) {
                      setActiveBottomTab('risk');
                    }
                  }
                  // Force editor layout update during resize
                  if (editorRef.current?.editor) {
                    editorRef.current.editor.layout();
                  }
                };
                
                const handleMouseUp = () => {
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                  document.body.style.cursor = '';
                  document.body.style.userSelect = '';
                  // Force editor layout update after resize
                  setTimeout(() => {
                    if (editorRef.current?.editor) {
                      editorRef.current.editor.layout();
                    }
                  }, 100);
                };
                
                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
                document.body.style.cursor = 'row-resize';
                document.body.style.userSelect = 'none';
              }}
            />
            {/* Panel Tabs */}
            <div className={`flex items-center bg-muted/30 h-9 flex-shrink-0 ${bottomPanelHeight > 36 ? 'border-b' : ''} relative z-0`}>
              {currentJobId && (
                <button
                  onClick={() => setActiveBottomTab('results')}
                  className={`px-4 h-full text-xs font-medium border-r border-border flex items-center gap-2 ${
                    activeBottomTab === 'results'
                      ? 'bg-background text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <BarChart3 className="w-3.5 h-3.5" />
                  Results
                  {job?.status === 'completed' && (
                    <Badge variant="outline" className="ml-1 h-4 px-1 text-[10px]">
                      {result?.rowCount || '✓'}
                    </Badge>
                  )}
                  {(job?.status === 'queued' || job?.status === 'processing') && (
                    <Loader2 className="w-3 h-3 ml-1 animate-spin" />
                  )}
                </button>
              )}
      {riskAnalysisMode && (
                <button
                  onClick={() => setActiveBottomTab('risk')}
                  className={`px-4 h-full text-xs font-medium border-r border-border flex items-center gap-2 ${
                    activeBottomTab === 'risk'
                      ? 'bg-background text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <TrendingUp className="w-3.5 h-3.5" />
                  Risk & Signals Metrics
                  {!riskAnalysisAvailable && (
                    <Loader2 className="w-3 h-3 ml-1 animate-spin" />
                  )}
                </button>
              )}
              {!isNew && currentScript?.id && (
                ((currentScript?.strategyMetadata && (currentScript.strategyMetadata as any)?.signals?.entry) || backtestJobId) && (
                  <button
                    onClick={() => setActiveBottomTab('backtests')}
                    className={`px-4 h-full text-xs font-medium border-r border-border flex items-center gap-2 ${
                      activeBottomTab === 'backtests'
                        ? 'bg-background text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <BarChart3 className="w-3.5 h-3.5" />
                    Backtests
                  </button>
                )
              )}
              <div className="flex-1" />
              {/* Panel Controls */}
              <div className="flex items-center gap-1 px-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleBottomPanel}
                  className="h-6 w-6"
                  title={bottomPanelHeight <= 36 ? "Expand panel" : "Minimize panel"}
                >
                  {bottomPanelHeight <= 36 ? (
                    <Maximize2 className="w-3 h-3" />
                  ) : (
                    <Minimize2 className="w-3 h-3" />
                  )}
                </Button>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-none"
                onClick={() => {
                  const minHeight = 36;
                  const hasContent = currentJobId || riskAnalysisMode || backtestJobId;
                  // If bottomPanelHeight is 0 but content exists, treat as 36px (minimized)
                  const effectiveHeight = bottomPanelHeight > 0 ? bottomPanelHeight : (hasContent ? 36 : 0);
                  
                  if (effectiveHeight > minHeight) {
                    // Collapse to just tab bar (minimize to 36px)
                    collapseBottomPanel(true);
                  } else if (effectiveHeight === minHeight) {
                    // Hide completely (set to 0)
                    collapseBottomPanel(false);
                  } else {
                    // Expand from hidden (0px) - same as toggle
                    toggleBottomPanel();
                  }
                }}
                title={
                  bottomPanelHeight > 36
                    ? "Minimize panel"
                    : bottomPanelHeight === 36
                    ? "Close panel"
                    : "Open panel"
                }
              >
                {bottomPanelHeight > 36 ? (
                  <ChevronDown className="w-4 h-4" />
                ) : bottomPanelHeight === 36 ? (
                  <X className="w-4 h-4" />
                ) : (
                  <ChevronUp className="w-4 h-4" />
                )}
              </Button>
            </div>

            {/* Panel Content - only show if panel is expanded beyond tab bar height (36px) */}
            {(() => {
              const shouldShowPanel = bottomPanelHeight > 36;
              if (activeBottomTab === 'results' && !shouldShowPanel) {
                console.log('⚠️ Results tab active but panel not expanded:', { bottomPanelHeight, activeBottomTab });
              }
              return shouldShowPanel;
            })() && (
            <div className="flex-1 overflow-auto min-h-0" style={{ height: `calc(${bottomPanelHeight}px - 36px)` }}>
              {(() => {
                if (activeBottomTab === 'results') {
                  return (
                    <div className="h-full p-4 space-y-4">
                      {/* Date range + Run Analysis — always visible on Results tab */}
                      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-muted/30 px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <Label className="text-xs font-medium whitespace-nowrap">{t('results.dateRange', 'Date range')}</Label>
                        </div>
                        <Select
                          value={resultsDatePreset || 'custom'}
                          onValueChange={(value) => {
                            if (value === 'custom') {
                              setResultsDatePreset('')
                              return
                            }
                            setResultsDatePreset(value)
                            const { startDate, endDate } = getResultsDateRangeFromPreset(value)
                            setResultsDateStart(startDate)
                            setResultsDateEnd(endDate)
                          }}
                        >
                          <SelectTrigger className="h-8 w-[140px]">
                            <SelectValue placeholder={t('results.quickPresets', 'Quick presets')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="custom">{t('results.custom', 'Custom')}</SelectItem>
                            {resultsDatePresets.map((p) => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-2">
                          <Input
                            type="date"
                            value={resultsDateStart}
                            onChange={(e) => {
                              setResultsDateStart(e.target.value)
                              setResultsDatePreset('')
                            }}
                            className="h-8 w-[140px]"
                          />
                          <span className="text-muted-foreground text-sm">–</span>
                          <Input
                            type="date"
                            value={resultsDateEnd}
                            onChange={(e) => {
                              setResultsDateEnd(e.target.value)
                              setResultsDatePreset('')
                            }}
                            className="h-8 w-[140px]"
                          />
                        </div>
                        <Button
                          size="sm"
                          onClick={handleQuickRunAnalysis}
                          disabled={quickRunParamsUnchanged}
                          className="h-8 gap-1.5"
                        >
                          <Play className="h-3.5 w-3.5" />
                          {t('results.runAnalysis', 'Run Analysis')}
                        </Button>
                      </div>

                      {currentJobId ? (
                        job ? (
                          renderResultsContent()
                        ) : (
                          <div className="flex items-center justify-center flex-1 min-h-[200px]">
                            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                          </div>
                        )
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {t('results.setDateAndRun', 'Set date range (optional) and click Run Analysis to execute.')}
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
              {activeBottomTab === 'risk' && riskAnalysisMode && (
                <div className="h-full overflow-auto p-4">
          <SignalRiskWidget
            dslCode={dslCode || ''}
            datasetId={selectedDatasetId}
            scriptId={currentScript?.id && !isNew ? currentScript.id : undefined}
            onAnalysisComplete={handleRiskAnalysisComplete}
            onAnalysisError={handleRiskAnalysisError}
            onSendToAIAssistant={handleSendRiskToAIAssistant}
          />
        </div>
      )}
              {activeBottomTab === 'backtests' && !isNew && currentScript?.id && (
                <div className="h-full overflow-auto p-4">
                  <BacktestTabs 
                    scriptId={currentScript.id}
                    initialJobId={backtestJobId}
                    onClose={() => {
                      setActiveBottomTab(null)
                      setBacktestJobId(null)
                    }}
                  />
                </div>
              )}
              {!activeBottomTab && (
                <div className="flex items-center justify-center py-2 text-muted-foreground text-xs flex-shrink-0">
                  Select a tab to view content
                </div>
              )}
            </div>
            )}
          </div>
            );
          })()}
          </div>
        </div>
      </div>

      {/* Legacy Results Panel - Keep for now but will be removed */}
      {false && currentJobId && job && (
        <Card className="mt-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle>{t('jobs.result')}</CardTitle>
                <Badge variant={
                  job?.status === 'completed' ? 'default' :
                  job?.status === 'failed' ? 'destructive' :
                  job?.status === 'processing' ? 'secondary' : 'outline'
                }>
                  {job?.status}
                </Badge>
                {job?.progress !== undefined && (
                  <span className="text-sm text-muted-foreground">{job?.progress}%</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {job?.status === 'completed' && result && (result.resultType === 'table' || result.resultType === 'stats') && Array.isArray(result.data) && (
                  <>
                    <Button
                      variant={viewMode === 'table' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('table')}
                    >
                      <Table className="w-4 h-4 mr-2" />
                      {t('results.table')}
                    </Button>
                    <Button
                      variant={viewMode === 'chart' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('chart')}
                    >
                      <BarChart3 className="w-4 h-4 mr-2" />
                      {t('results.chart')}
                    </Button>
                    <div className="w-px h-6 bg-border mx-1" />
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setIsAIAnalysisOpen(true)}
                              disabled={dataSizeCheck.tooLarge}
                            >
                              <Brain className="w-4 h-4 mr-2" />
                              {t('results.analyzeWithAI')}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {dataSizeCheck.tooLarge && (
                          <TooltipContent>
                            <p className="max-w-xs">{dataSizeCheck.reason}</p>
                            <p className="text-xs mt-1">{t('results.aiAnalysisTooLarge', 'Data is too large for AI analysis. Please reduce the dataset size.')}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (Array.isArray(result.data)) {
                          exportToCSV(result.data, `results-${currentJobId}-${Date.now()}.csv`)
                          showSuccessToast(t('results.exportCSVSuccess'))
                        }
                      }}
                      disabled={!Array.isArray(result.data)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      CSV
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        exportToJSON(result.data || result, `results-${currentJobId}-${Date.now()}.json`)
                        showSuccessToast(t('results.exportJSONSuccess'))
                      }}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      JSON
                    </Button>
                  </>
                )}
                {job?.status === 'completed' && result && result.resultType !== 'table' && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        exportToJSON(result.data || result, `results-${currentJobId}-${Date.now()}.json`)
                        showSuccessToast(t('results.exportJSONSuccess'))
                      }}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      JSON
                    </Button>
                  </>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setResultsExpanded(!resultsExpanded)}
                >
                  {resultsExpanded ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setCurrentJobId(null)
                    setResultsExpanded(false)
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {(() => {
              if (!job || job!.progress === undefined || job!.status === 'completed' || job!.status === 'failed') return null
              return <Progress value={job!.progress} className="mt-2" />
            })()}
          </CardHeader>
          {resultsExpanded && (
            <CardContent>
              {(() => {
                if (!job) return null
                if (job!.status !== 'queued' && job!.status !== 'processing') return null
                return (
                  <div className="text-center py-8 text-muted-foreground">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                    <p>
                      {job!.status === 'queued' ? t('jobs.queued') : t('jobs.processing')}
                    </p>
                    {job!.progress !== undefined && (
                      <p className="text-sm mt-2">{job!.progress}%</p>
                    )}
                    {isLoadingJob && (
                      <p className="text-xs mt-2 text-muted-foreground">Refreshing...</p>
                    )}
                  </div>
                )
              })()}
              {(() => {
                if (!job) return null
                if (job!.status !== 'failed') return null
                return (
                  <div className="p-4 bg-destructive/10 border border-destructive/20 rounded">
                    <p className="text-sm text-destructive font-medium mb-1">{t('jobs.error')}</p>
                    <p className="text-sm">{job!.error || t('jobs.unknownError')}</p>
                  </div>
                )
              })()}
              {(() => {
                if (!job) return null
                if (job!.status !== 'completed' || !result) return null
                return (
                <div className="space-y-4">
                  {result.resultType === 'stats' && Array.isArray(result.data) && (() => {
                    const cleanedData = cleanColumnNames(result.data);
                    const rawStatsTypes: Record<string, string> | undefined = (result as { metadata?: { statsColumnTypes?: Record<string, string>; isSummary?: boolean } }).metadata?.statsColumnTypes;
                    const isSummary = (result as { metadata?: { isSummary?: boolean } }).metadata?.isSummary === true;
                    const statsColumnTypesClean: Record<string, string> = Object.fromEntries(
                      Object.entries(rawStatsTypes ?? {}).map(([k, v]) => [cleanColumnName(k), v])
                    );
                    const hasGroupColumns = Object.values(statsColumnTypesClean).some((v) => v === 'group');
                    const isSingleRowSummary = cleanedData.length === 1 && !hasGroupColumns;
                    if (isSummary || isSingleRowSummary) {
                      const row = cleanedData[0] as Record<string, unknown>;
                      if (!row) return null;
                      return (
                        <div>
                          <h3 className="text-lg font-semibold mb-3">{t('results.statistics')}</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {Object.entries(row).map(([key, value]) => (
                              <div key={key} className="p-3 bg-muted rounded">
                                <div className="text-sm text-muted-foreground">{key}</div>
                                <div className="text-lg font-semibold">
                                  {typeof value === 'number' ? value.toLocaleString() : String(value)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div>
                        <h3 className="text-lg font-semibold mb-3">{t('results.statistics')}</h3>
                        {viewMode === 'table' ? (
                          <div className="space-y-2">
                            {cleanedData.length > 500 && (
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-muted-foreground">
                                  {showAllRows 
                                    ? t('results.showingAllRows', { total: cleanedData.length })
                                    : t('results.showingRows', { count: 500, total: cleanedData.length })
                                  }
                                </p>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setShowAllRows(!showAllRows)}
                                  className="flex items-center gap-2 h-7 text-xs"
                                >
                                  {showAllRows ? (
                                    <>
                                      <ChevronUp className="w-3 h-3" />
                                      {t('results.hideRows', 'Hide Rows')}
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="w-3 h-3" />
                                      {t('results.showAllRows', 'Show All Rows')}
                                    </>
                                  )}
                                </Button>
                              </div>
                            )}
                            <div className="border rounded-md overflow-auto max-h-[400px]">
                              <table className="w-full text-sm">
                                <thead className="bg-muted">
                                  <tr>
                                    {cleanedData.length > 0 && Object.keys(cleanedData[0] as Record<string, unknown>).map((key) => (
                                      <th key={key} className="px-4 py-2 text-left font-medium">{key}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {(cleanedData.length > 500 && !showAllRows 
                                    ? cleanedData.slice(0, 500) 
                                    : cleanedData
                                  ).map((row: any, idx: number) => (
                                    <tr key={idx} className="border-t">
                                      {Object.values(row).map((value: any, colIdx: number) => (
                                        <td key={colIdx} className="px-4 py-2">
                                          {typeof value === 'number' ? value.toLocaleString() : String(value)}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {cleanedData.length <= 500 && cleanedData.length > 100 && (
                                <div className="p-2 text-xs text-muted-foreground text-center border-t">
                                  {t('results.showingRows', { count: 100, total: cleanedData.length })}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <ResultsChart
                            data={cleanedData}
                            metadata={{ statsColumnTypes: statsColumnTypesClean }}
                            resultType={result.resultType}
                          />
                        )}
                      </div>
                    );
                  })()}
                  {result.resultType === 'stats' && !Array.isArray(result.data) && result.data && typeof result.data === 'object' && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">{t('results.statistics')}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(result.data as Record<string, unknown>).map(([key, value]) => (
                          <div key={key} className="p-3 bg-muted rounded">
                            <div className="text-sm text-muted-foreground">{key}</div>
                            <div className="text-lg font-semibold">
                              {typeof value === 'number' ? value.toLocaleString() : String(value)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {result.resultType === 'stats' && !Array.isArray(result.data) && (!result.data || typeof result.data !== 'object') && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">{t('results.statistics')}</h3>
                      <pre className="text-xs bg-muted p-4 rounded-md overflow-auto">
                        {JSON.stringify(result.data, null, 2)}
                      </pre>
                    </div>
                  )}
                  {result.resultType === 'table' && Array.isArray(result.data) && (() => {
                    // Clean column names to remove hash suffixes
                    const cleanedData = cleanColumnNames(result.data);
                    return (
                      <div>
                        <h3 className="text-lg font-semibold mb-3">
                          {t('results.data')} ({result.rowCount || cleanedData.length} {t('results.rows')})
                        </h3>
                        {viewMode === 'table' ? (
                          <div className="space-y-2">
                            {cleanedData.length > 500 && (
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-muted-foreground">
                                  {showAllRows 
                                    ? t('results.showingAllRows', { total: cleanedData.length })
                                    : t('results.showingRows', { count: 500, total: cleanedData.length })
                                  }
                                </p>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setShowAllRows(!showAllRows)}
                                  className="flex items-center gap-2 h-7 text-xs"
                                >
                                  {showAllRows ? (
                                    <>
                                      <ChevronUp className="w-3 h-3" />
                                      {t('results.hideRows', 'Hide Rows')}
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="w-3 h-3" />
                                      {t('results.showAllRows', 'Show All Rows')}
                                    </>
                                  )}
                                </Button>
                              </div>
                            )}
                            <div className="border rounded-md overflow-auto max-h-[400px]">
                              <table className="w-full text-sm">
                                <thead className="bg-muted">
                                  <tr>
                                    {cleanedData.length > 0 && Object.keys(cleanedData[0] as Record<string, unknown>).map((key) => (
                                      <th key={key} className="px-4 py-2 text-left font-medium">{key}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {(cleanedData.length > 500 && !showAllRows 
                                    ? cleanedData.slice(0, 500) 
                                    : cleanedData
                                  ).map((row: any, idx: number) => (
                                    <tr key={idx} className="border-t">
                                      {Object.values(row).map((value: any, colIdx: number) => (
                                        <td key={colIdx} className="px-4 py-2">
                                          {typeof value === 'number' ? value.toLocaleString() : String(value)}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {cleanedData.length <= 500 && cleanedData.length > 100 && (
                                <div className="p-2 text-sm text-muted-foreground text-center">
                                  {t('results.showingRows', { count: 100, total: cleanedData.length })}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <ResultsChart
                              data={cleanedData}
                              metadata={(() => {
                                const raw = (result as { metadata?: { statsColumnTypes?: Record<string, string> } }).metadata?.statsColumnTypes ?? {};
                                return Object.keys(raw).length > 0 ? { statsColumnTypes: Object.fromEntries(Object.entries(raw).map(([k, v]) => [cleanColumnName(k), v])) } : undefined;
                              })()}
                              resultType={result.resultType}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {result.resultType === 'signals' && result.data && (() => {
                    // Clean column names to remove hash suffixes
                    const cleanedData = Array.isArray(result.data) ? cleanColumnNames(result.data) : result.data;
                    return (
                      <div>
                        <h3 className="text-lg font-semibold mb-3">{t('results.resultType.signals')}</h3>
                        {Array.isArray(cleanedData) ? (
                          <div className="space-y-2">
                            {cleanedData.length > 500 && (
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-muted-foreground">
                                  {showAllRows 
                                    ? t('results.showingAllRows', { total: cleanedData.length })
                                    : t('results.showingRows', { count: 500, total: cleanedData.length })
                                  }
                                </p>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setShowAllRows(!showAllRows)}
                                  className="flex items-center gap-2 h-7 text-xs"
                                >
                                  {showAllRows ? (
                                    <>
                                      <ChevronUp className="w-3 h-3" />
                                      {t('results.hideRows', 'Hide Rows')}
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="w-3 h-3" />
                                      {t('results.showAllRows', 'Show All Rows')}
                                    </>
                                  )}
                                </Button>
                              </div>
                            )}
                            <div className="border rounded-md overflow-auto max-h-[400px]">
                              <table className="w-full text-sm">
                                <thead className="bg-muted">
                                  <tr>
                                    {cleanedData.length > 0 && Object.keys(cleanedData[0] as Record<string, unknown>).map((key) => (
                                      <th key={key} className="px-4 py-2 text-left font-medium">{key}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {(cleanedData.length > 500 && !showAllRows 
                                    ? cleanedData.slice(0, 500) 
                                    : cleanedData
                                  ).map((row: any, idx: number) => (
                                    <tr key={idx} className="border-t">
                                      {Object.values(row).map((value: any, colIdx: number) => (
                                        <td key={colIdx} className="px-4 py-2">
                                          {typeof value === 'number' ? value.toLocaleString() : String(value)}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {cleanedData.length <= 500 && cleanedData.length > 100 && (
                                <div className="p-2 text-sm text-muted-foreground text-center">
                                  {t('results.showingRows', { count: 100, total: cleanedData.length })}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <pre className="text-xs bg-muted p-4 rounded-md overflow-auto">
                            {JSON.stringify(cleanedData, null, 2)}
                          </pre>
                        )}
                      </div>
                    );
                  })()}
                  {result.resultType === 'signals' && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold">
                          {t('results.resultType.signals')}
                          {signalsData.length > 0 && (
                            <span className="ml-2 text-sm font-normal text-muted-foreground">
                              ({signalsData.length} {signalsHasMore ? '+' : ''} signals)
                            </span>
                          )}
                        </h3>
                        {signalsHasMore && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={loadMoreSignals}
                            disabled={isLoadingMoreSignals}
                          >
                            {isLoadingMoreSignals ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Loading...
                              </>
                            ) : (
                              'Load More'
                            )}
                          </Button>
                        )}
                      </div>
                      {signalsData.length > 0 ? (
                        <div className="border rounded-md overflow-auto max-h-[400px]">
                          <table className="w-full text-sm">
                            <thead className="bg-muted sticky top-0">
                              <tr>
                                {signalsColumns.length > 0 ? (
                                  signalsColumns.map((key) => (
                                    <th key={key} className="px-4 py-2 text-left font-medium">{key}</th>
                                  ))
                                ) : (
                                  Object.keys(signalsData[0] as Record<string, unknown>).map((key) => (
                                    <th key={key} className="px-4 py-2 text-left font-medium">{key}</th>
                                  ))
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {signalsData.map((row: any, idx: number) => (
                                <tr key={idx} className="border-t hover:bg-muted/50">
                                  {signalsColumns.length > 0 ? (
                                    signalsColumns.map((col) => (
                                      <td key={col} className="px-4 py-2">
                                        {typeof row[col] === 'number' 
                                          ? row[col].toLocaleString() 
                                          : String(row[col] ?? '')}
                                      </td>
                                    ))
                                  ) : (
                                    Object.values(row).map((value: any, colIdx: number) => (
                                      <td key={colIdx} className="px-4 py-2">
                                        {typeof value === 'number' ? value.toLocaleString() : String(value)}
                                      </td>
                                    ))
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {signalsHasMore && (
                            <div className="p-4 text-center border-t">
                              <Button
                                variant="outline"
                                onClick={loadMoreSignals}
                                disabled={isLoadingMoreSignals}
                              >
                                {isLoadingMoreSignals ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Loading more signals...
                                  </>
                                ) : (
                                  `Load More (Page ${signalsPage + 1})`
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>No signals found</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                )
              })()}
              {job?.status === 'completed' && !result && (
                <div className="text-center py-8 text-muted-foreground">
                  <p>{t('results.loading')}</p>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* AI Configuration Modal */}
      <AIConfigModal
        open={aiConfigOpen}
        onOpenChange={setAiConfigOpen}
      />

      {/* Backtest Configuration Modal */}
      {!isNew && scriptId && (
        <BacktestConfigModal
          scriptId={scriptId}
          dslCode={dslCode || ''}
          open={showBacktestConfig}
          onOpenChange={setShowBacktestConfig}
          onConfigSaved={() => {
            showSuccessToast('Backtest configuration saved');
            // Reload script to get updated strategyMetadata
            loadScript(scriptId);
          }}
        />
      )}

      {/* Unsaved Changes Dialog */}
      <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes to your script. Are you sure you want to leave? Your changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleUnsavedDialogCancel}>
              Stay and Save
            </Button>
            <Button variant="destructive" onClick={handleUnsavedDialogConfirm}>
              Leave Without Saving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validation Error Modal (Create/Save with invalid form) */}
      <Dialog
        open={validationErrorModal.open}
        onOpenChange={(open) => setValidationErrorModal((prev) => ({ ...prev, open }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('errors.saveScript')}</DialogTitle>
            <DialogDescription>{validationErrorModal.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setValidationErrorModal({ open: false, message: '' })}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Analysis Dialog */}
      <Dialog open={isAIAnalysisOpen} onOpenChange={setIsAIAnalysisOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5" />
              {t('results.aiAnalysis')}
            </DialogTitle>
            <DialogDescription>
              {t('results.aiAnalysisDescription')}
            </DialogDescription>
          </DialogHeader>
          {result?.data && (
            <AIResultsExplanation
              results={
                Array.isArray(result.data)
                  ? { data: result.data, count: result.data.length }
                  : (result.data as Record<string, any>)
              }
              scriptDescription={currentScript?.description || currentScript?.name}
              scriptCode={currentScript?.dslCode}
              datasetInfo={{
                symbol: dataset?.metadata?.symbol as string | undefined,
                timeframe: dataset?.metadata?.timeframe as string | undefined,
                columns: dataset?.metadata?.columns as string[] | undefined,
                timezone: dataset?.metadata?.timezone as string | undefined,
                sessionStrategy: dataset?.metadata?.session_strategy as 'by_date' | 'custom' | 'trading_sessions' | undefined,
                sessions: dataset?.metadata?.sessions as Array<{
                  name: string;
                  start_time: string;
                  end_time: string;
                  description?: string;
                }> | undefined,
                sessionIncludeDate: dataset?.metadata?.session_include_date as boolean | undefined,
                intervalDuration: dataset?.metadata?.interval_duration as number | string | undefined,
                intervalIdFormat: dataset?.metadata?.interval_id_format as 'timestamp' | 'datetime' | undefined,
              }}
              resultType={result.resultType}
              scriptId={currentScript?.id}
              resultId={job?.resultId ?? undefined}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
